export const architectsTranslations = {
  jumbotron: {
    title: "Architects for Azaz",
    desc: "An International Competition To Redesign Homes for Internally Displaced Persons (IDPs) in Syria ",
  },
};
export const architectsSections = [
  {
    label: "Introduction",
    content: [
      {
        type: "plain",
        text: `The Architects for Azaz competition is an international call to action for architects and designers to create innovative, sustainable, and culturally sensitive housing solutions for the displaced people of Syria. The competition seeks to address the complex crisis in Azaz, northwestern Syria, by creating a new urban quarter that provides shelter and recreates a sense of home, community, and belonging. The competition is open to architects, designers, engineers, and students worldwide, and the winning design will be implemented in Azaz, Syria.`,
      },
    ],
  },
  {
    label: "History of Azaz; Northwestern Syria",
    content: [
      {
        type: "plain",
        text: `Azaz, Located in the northwest of Syria, has a deep and varied history, reflecting the broader stories of the region. It’s been an essential place for many empires and civilizations, as a critical fortress town and a main battleground during different times. After Syria gained independence, Azaz became the center of its district.
      Recently, Azaz has become a stable place in the middle of the chaos of the Syrian War. This stability has brought many NGOs to the area to start housing projects, giving shelter and help to those affected by the conflict. Its secure setting and location near the Turkish border make Azaz an excellent place for carrying out these projects. NGOs see the need for long-lasting housing solutions and have chosen Azaz as a central point for their work. As a hub for these projects, Azaz is providing a safe place, aiming to bring back stability, community, and hope to those who have lost their homes.`,
      },
    ],
  },
  {
    label: "Challenge",
    content: [
      {
        type: "plain",
        text: `Now in its twelfth year, the Syrian conflict has led to one of the most significant displacements in recent history, with over 12.5 million people uprooted. This crisis has left millions without homes and deeply affected the essence of "dwelling," eroding the sense of identity, community, and security.`,
      },
      {
        type: "titleDesc",
        title: "City Challenges",
        desc: `Azaz faces overcrowding due to an influx of 2.9 million refugees. The emergency housing need has led to the repurposing of agricultural land, posing risks to local food security and the environment. As a result of rapid development, the area's natural environment and lack of public spaces have been negatively impacted.`,
      },
      {
        type: "titleDesc",
        title: "IDP Challenges",
        desc: `The majority of IDPs live in harsh conditions with insufficient space and privacy, struggling to establish a new sense of belonging amid affordable housing shortages.`,
      },
      {
        type: "titleDesc",
        title: "NGO Challenges",
        desc: `Despite being at the forefront of relief, NGOs face resource constraints and the challenge of rapidly building quality, low-cost housing.`,
      },
    ],
  },
  {
    label: "Call to entry",

    content: [
      {
        type: "plain",
        text: `We invite the international architectural community to rise to the challenge of designing innovative, sustainable, and culturally sensitive housing solutions for the displaced people of Syria. This competition seeks to address the complex crisis in Azaz, northwestern Syria, by creating a new urban quarter that provides shelter and recreates a sense of home, community, and belonging.`,
      },
      {
        type: "link",
        link: "https://dewan-award.com/wp-content/uploads/2023/04/Basra-waterfront.pdf",
        text: "Download the full brief.",
      },
    ],
  },
  {
    label: "Response: Main Task",

    content: [
      {
        type: "titleDesc",
        title: "Design a Housing Complex",
        desc: `Participants are tasked with designing a housing complex that accommodates 500-600 families, incorporating cultural, commercial, educational, and public functions.`,
      },
      {
        type: "titleDesc",
        title: "Balance Social Responsibility and Community Diversity",
        desc: `The design must harmoniously balance social responsibility, community diversity, and vibrant public spaces, curating spaces that spark interactions and nurture a sense of community.`,
      },

      {
        type: "titleDesc",
        title: "Create Resilient and Adaptable Spaces",
        desc: `The design must be resilient and adaptable, considering future scenarios, changing community needs, and integrating public and private functions to foster a cohesive community fabric.`,
      },
      {
        type: "titleDesc",
        title: "Consider Cost Efficiency and Construction Feasibility",
        desc: `The design must be realistic, feasible, and cost-efficient, considering local resources, available skills, and budget constraints.`,
      },
    ],
  },

  {
    label: "Design Program",
    content: [
      {
        type: "h-list",
        text: [
          "Housing Units",
          "Public Square",
          "Youth Center",
          "Sport Facilities",
          "Daycare",
          "Administration",
          "Market",
          "Bakery",
        ],
      },
      {
        type: "link",
        link: "https://dewan-award.com/wp-content/uploads/2023/04/Basra-waterfront.pdf",
        text: "Download the detailed Design Programme.",
      },
    ],
  },
  {
    label: "Competition Site",
    content: [
      {
        type: "titleDesc",
        title: "Location",
        desc: "The competition site is located on the main road between Nayarah and Azaz in Northwestern Syria. This region has emerged as a stable hub amid the chaos of the Syrian Civil War, attracting NGOs to implement housing projects for those affected by the conflict.",
      },
      {
        type: "titleDesc",
        title: "Historical Value",
        desc: "The site bears significant historical value as a testament to the resilience of the local community in the face of adversity. Originally agricultural land, it has been repurposed multiple times to meet the community's changing needs, providing shelter to displaced persons and symbolizing the region's ongoing efforts to provide stable, secure housing.",
      },
      {
        type: "titleDesc",
        title: "Current Use",
        desc: "Encircled by various Molham housing projects, the site offers a unique perspective on the housing situation in Azaz. It has previously been utilized as a temporary housing area by the Molham Volunteering Team, reflecting the city's struggles and triumphs.",
      },
    ],
  },
  {
    label: "Data Package",
    content: [
      {
        type: "iconsWithLinks",
        text: "Information Package",
        icon: "file",
        link: "https://drive.google.com/drive/folders/1Ie8E8-92hm7zb-vcGd0MPz0a1LuXav3E?usp=drive_link",
      },
      {
        type: "iconsWithLinks",
        text: "Location",
        icon: "map",
        link: "https://goo.gl/maps/WnCvSQ9VNaNCXENZA",
      },
      {
        type: "iconsWithLinks",
        text: "Site Videos",
        icon: "video",
        link: "https://www.youtube.com/playlist?list=PL_GWh2JX1N86Is-QkwU8t2vDi15gAYq0t",
      },
    ],
  },
  {
    label: "Criteria",

    content: [
      {
        type: "titleDesc",
        title: "Design",
        desc: "Project evaluations will focus on four key areas: the design's capacity to foster community interaction and connection; the incorporation of sustainable, energy-efficient practices and local materials; the promotion of personal connection and pride among residents; and the design's resilience to climatic and seismic changes, ensuring durability and safety.",
      },
      {
        type: "titleDesc",
        title: "Functionality",
        desc: "Design adaptability to meet the community's evolving needs will be crucial in the evaluation process. This includes the thoughtful planning of communal areas to encourage social integration and foster a sense of ownership. The organization and hierarchy of spaces will be assessed for clarity and usability, ensuring a smooth flow within the living environment. A key focus will also be maintaining a balanced approach to privacy, ensuring that individual needs are respected within the communal living setup.",
      },
      {
        type: "titleDesc",
        title: "Execution",
        desc: "The evaluation will emphasize the scalability of designs, assessing their adaptability to various contexts. Cost efficiency will also be a key factor, focusing on economic construction methods that maintain quality. Furthermore, the feasibility of the designs will be judged based on their alignment with local resources, available skills, and budget constraints, ensuring practical and sustainable implementation.",
      },
    ],
  },
  {
    isTimeline: true,
    label: "Competition Schedule",
    content: [
      {
        date: "Dec 25, 2023",
        desc: "Competition launch",
      },
      {
        date: "Jan 10, 2023",
        desc: "Deadline for Receiving Questions",
      },
      {
        date: "Jan 2023",
        desc: "Molham Projects seminar",
      },
      {
        date: "Mar 14, 2024",
        desc: "Registration deadline",
      },
      {
        date: "Mar 15, 2024",
        desc: "Submission deadline",
      },
      {
        date: "Apr 2024",
        desc: "Shortlist announcement",
      },
      {
        date: "Apr 2024",
        desc: "Winner announcement",
      },
    ],
    deadline: "All deadlines are at 23:59 GMT.",
  },
  {
    label: "Eligibility",
    content: [
      {
        type: "plain",
        text: "The competition is open to individuals and multidisciplinary teams of up to 6 members, including architects, urban designers, engineers, students, social workers, sociologists, and other design enthusiasts. Collaboration across specialties is encouraged to foster innovative and holistic solutions",
      },
    ],
  },
  {
    label: "Regulations",
    content: [
      {
        type: "o-list",

        text: [
          "This is an anonymous competition, and the Unique Registration Number is the only means of identification.",
          "The official language of the award is English.",
          "The registration fee for this award is non-refundable.",
          "Contacting the jury is prohibited.",
          "As the award organizer, Mimaria Studio reserves the right to modify the schedule if necessary.",
          "Entries are only reviewed if rules or submission requirements are followed.",
          "Participation assumes acceptance of the regulations.",
          "By participating in this competition, entrants acknowledge that the Molham team retains the right to alter, adjust, or adapt the winning designs for implementation or other related purposes to ensure they comply with local regulations and are feasible. However, the designers will retain the intellectual property rights of the original designs.",
        ],
      },
    ],
  },
  {
    label: "Submission Requirements",

    content: [
      {
        type: "titleDesc",
        title: "A0 Board",
        desc: "Submit one A0 board in JPEG format at 300 dpi resolution. Include all necessary information to explain your proposal, such as the modules plan, master plan, plans, sections, elevations, visualizations, and diagrams. Place the unique registration number in the upper left corner of the board in 22pt font.",
      },
      {
        type: "titleDesc",
        title: "Project Statement",
        desc: "Include a Word file containing a concise project statement (maximum 250 words) explaining the design proposal.",
      },
      {
        type: "titleDesc",
        title: "Main View",
        desc: "Provide an independent one view of the entry in JPEG format (16:9 ratio), preferably a bird's eye view of the project at 300 dpi resolution.",
      },
      {
        type: "titleDesc",
        title: "Submission Method",
        desc: "Use the upload link in your registration confirmation email to submit your entry.",
      },
      {
        type: "titleDesc",
        title: "Poster Layout",
        desc: "You should use landscape layout for your posters.",
      },
      {
        type: "titleDesc",
        title: "Zip File Contents",
        desc: "Your submission should be contained in a zip file, including 1 A0 board (JPEG – 300 dpi), 1 Word document (250 words max), and one central image (16:9) of the project (JPEG – 300 dpi).",
      },
    ],
  },
  {
    label: "Prizes",
    content: [
      {
        type: "h-list",
        text: [
          "First Place: $10,000",
          "Second Place: $5,000",
          "Third Place: $3,000",
          "20 Honorable Mentions (certificates & publication)",
        ],
      },
    ],
  },
] as ArchitectSection[];
export type ArchitectSection = {
  label: string;
  content: content[];
  isTimeline?: boolean;
  deadline?: string;
};
export type plainText = {
  text: string;
  type?: "plain";
};
export type titleDesc = {
  title: string;
  desc: string;
  type?: "titleDesc";
};
export type link = {
  link: string;
  text: string;
  type?: "link";
};
export type hList = {
  text: string[];
  type?: "h-list";
};
export type oList = {
  text: string[];
  type?: "o-list";
};
export type iconsWithLinks = {
  text: string;
  icon: string;
  link: string;
  type?: "iconsWithLinks";
};
export type timeline = {
  date: string;
  desc: string;
  deadline?: string;
  type?: "timeline";
};
export type content =
  | plainText
  | titleDesc
  | link
  | hList
  | iconsWithLinks
  | timeline
  | oList;
export type typesOfContent =
  | "plain"
  | "titleDesc"
  | "link"
  | "h-list"
  | "o-list"
  | "iconsWithLinks"
  | "timeline";

export type TJury = {
  name: string;
  title: string;
  desc: string;
};

export const jury: TJury[] = [
  {
    name: "Christoph Wessling",
    title:
      "Urban planner, Architect and Academic Visionary - Brandenburg University of Technology (BTU)",
    desc: "Christoph Wessling is an esteemed freelance urban planner and architect from Berlin with a distinguished career spanning over three decades in urban development planning and design. Since the early 2000s, he has been an influential researcher and lecturer at the Brandenburg University of Technology (BTU). His academic contributions include co-founding the double degree master's program in Urban Design - Revitalization of Historic City Districts, a collaboration with Cairo and Alexandria University initiated in 2014. From 2016 to 2019, Christoph dedicated his expertise as a mentor for the integrated urban development concept for Chernivtsi. In a significant role between 2020 and 2023, he served as the acting professor of Urban Planning and Urban Regeneration at the Technische Universität Berlin. His commitment to urban development extends beyond academia, as evidenced by his membership in the Pan-Ukraine Initiative, where he plays a crucial role in shaping urban landscapes and policies. Wessling's extensive experience and visionary approach to urban planning and regeneration make him a key figure in architectural and urban development circles in Germany and internationally.",
  },
  {
    name: "Mouaz Abouzaid",
    title: "Founder & Design Principal  at verform",
    desc: "Mouaz Abouzaid, a graduate of Alexandria University and the Founder & Design Principal of VERFORM, is a celebrated figure in the architecture industry, mainly known for his contributions in the Middle East. His visionary approach to architecture emphasizes sustainable, people-centric designs, reflecting his deep understanding of the interplay between human interactions and built environments. With over 17 years of experience, Mouaz has developed a rich portfolio that includes projects addressing critical societal issues. His notable project, Sheltainer City, ingeniously transformed shipping containers into functional living spaces for Cairo's low-income citizens, illustrating his dedication to solving urban challenges with creative solutions. Another significant achievement is the X-Space project in Dubai, which earned the World Architecture Festival's top prize in the Smart Cities category. This project reinvented a section of Sheikh Zayed Road into a pedestrian-friendly green space, showcasing Mouaz's expertise in urban regeneration and commitment to sustainable design principles. Mouaz's architectural practice at VERFORM focuses on addressing socio-economic issues through smart design solutions. His work transcends traditional architectural boundaries, offering innovative approaches to urban planning and design and significantly enhancing the quality of life in urban spaces.",
  },
  {
    name: "Islam Mosleh El Mashtooly",
    title: "Architect & Urbanist, founder of design & more",
    desc: `An architect with over 19 years of professional experience, currently holds the position of Design Director at OBM International (OBMI). His architectural journey is marked by significant roles highlighting his commitment to community-enhancing and culturally resonant design. In his role at OBMI, El Mashtooly has been instrumental in developing the AERA vertical resort concept, showcasing his dedication to sustainable urban space utilization. His portfolio includes prominent projects like Uptown Makkah in Saudi Arabia, Etihad Airways HQ in Abu Dhabi, and the Shelter on the Edge project in Syria, reflecting his unique blend of functionality and innovative design. Before joining OBMI, El Mashtooly served as the Lead Designer at Perkins+Will, where he further honed his design skills and contributed to several high-profile architectural projects. This role allowed him to integrate his philosophy of creating spaces that foster harmony and reflect the unique characteristics of the MENA region's climate and culture. El Mashtooly's exceptional design skills have been recognized internationally. His "JANNAT MASJID" project received the "Future Projects" award at the World Architecture Festival Awards. He has been named among the Top 50 Most Influential Architects in the Middle East for five consecutive years. He is a two-time recipient of the United International Architects Award in Baku.`,
  },
  {
    name: "Atef Nanoua",
    title: "Architect, CEO, & Cofounder at Molham Volunteering Team",
    desc: "An architect and co-founder of the Molham Volunteering Team graduated in Architecture from Philadelphia University in Jordan in 2015. His significant contributions to both humanitarian and architectural fields are noteworthy. As the Executive Director of the Molham Volunteering Team, Nanou has played a vital role in overseeing various projects and initiatives, blending his architectural expertise with a passion for social good. He designed the Fahd Raee School in Azaz city, demonstrating a unique commitment to leveraging architecture for societal benefit.",
  },
  {
    name: "Yaser Biyoush",
    title:
      "Veteran Civil engineer & Head of Engineering Department at Molham Volunteering Team",
    desc: "A veteran civil engineer who graduated from the University of Aleppo in 1984, leads the Engineering Department at the Molham Volunteering Team. Overseeing numerous engineering projects across Syria, Biyoush's extensive experience includes a tenure as a Structural Studies Auditor at the Engineering Syndicate and a role as a Supervising Engineer in a Dubai construction company. His current position reflects his commitment to applying his vast engineering knowledge to humanitarian and developmental projects within Syria.",
  },
  {
    name: "Abdulhadi Al Omar",
    title:
      "Architect & Head of Architectural Department  at Molham Volunteering Team",
    desc: "An architect working with the Molham Volunteering Team, has achieved notable success in architectural design. He has overseen and contributed to designing several key projects, including the prominent Gaza School in Northern Syria. His expertise extends to residential project design for the team, showcasing his skill and dedication to creating impactful architectural solutions. Al Omar's role in the Molham Volunteering Team highlights his commitment to using architectural design to improve society and community development.",
  },
];
